import {Color} from '@benchling/nucleus-ui';
import {Box, Group, Paper, Text} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {getExtensionIcon} from 'dev-platform/extensionIconMapper';

import {Extension, type ExtensionLabelSpec, registryLabelsForExtensionHeader} from './extensionConstants';
import ExtensionTags from './ExtensionTags';

const ICON_SIZE = 40;

interface ExtensionCardProps {
  extension: Extension;
  labelSpecs: ReadonlyArray<ExtensionLabelSpec>;
  onClick?: () => void;
}

export default function ExtensionCard({extension, labelSpecs, onClick}: ExtensionCardProps): JSX.Element {
  const displayName = extension.displayName ?? extension.id;

  const handleClick = (): void => {
    onClick?.();
  };

  return (
    <Paper
      component="button"
      type="button"
      disabled={!onClick}
      className={css(styles.card, onClick && styles.cardClickable)}
      withBorder={true}
      radius={4}
      onClick={handleClick}
    >
      <div className={css(styles.content)}>
        <Box className={css(styles.icon)} w={ICON_SIZE} h={ICON_SIZE}>
          {getExtensionIcon(extension.id, {
            className: css(styles.iconImg, styles.iconPlaceholder),
            extensionType: extension.extensionType,
          })}
        </Box>

        <div className={css(styles.textArea)}>
          <div className={css(styles.nameTaglineBlock)}>
            <Group spacing={4} align="center" noWrap={true}>
              <Text className={css(styles.nameText)} component="span" truncate={true}>
                {displayName}
              </Text>
              <Text className={css(styles.publisherText)} component="span">
                by {extension.provider.name}
              </Text>
            </Group>

            <div className={css(styles.taglineSlot)}>
              {extension.tagLine != null && extension.tagLine !== '' && (
                <Text className={css(styles.taglineText)} lineClamp={2}>
                  {extension.tagLine}
                </Text>
              )}
            </div>
          </div>

          <Group spacing={4} noWrap={true} className={css(styles.miniTags)}>
            <ExtensionTags
              extensionType={extension.extensionType}
              registryLabels={registryLabelsForExtensionHeader(extension.labels, labelSpecs)}
            />
          </Group>
        </div>
      </div>
    </Paper>
  );
}

const styles = StyleSheet.create({
  card: {
    textAlign: 'left',
    userSelect: 'none',
    width: '100%',
    padding: 12,
    boxSizing: 'border-box',
    borderColor: Color.GRAY_200,
    backgroundColor: Color.WHITE,
    borderRadius: 4,
    transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
  },
  cardClickable: {
    cursor: 'pointer',
    ':hover': {
      borderColor: Color.BLUE_450,
      boxShadow: '0 2px 6px rgba(162,185,190,0.4)',
    },
    ':focus': {
      outline: 'none',
      borderColor: Color.BLUE_450,
      boxShadow: `0 0 0 2px ${Color.BLUE_200}`,
    },
  },
  content: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
  },
  icon: {
    flexShrink: 0,
    borderRadius: 3,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  iconImg: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
  },
  iconPlaceholder: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: '100%',
    fontSize: 24,
    borderRadius: 4,
  },
  nameText: {
    fontSize: 14,
    fontWeight: 500,
    lineHeight: '16px',
    color: Color.GRAY_900,
  },
  publisherText: {
    fontSize: 10,
    lineHeight: '12px',
    color: Color.GRAY_600,
    flexShrink: 0,
  },
  taglineText: {
    fontSize: 13,
    lineHeight: '16px',
    color: Color.GRAY_900,
  },
  textArea: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    minWidth: 0,
  },
  nameTaglineBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  taglineSlot: {
    height: 32,
    overflow: 'hidden',
  },
  miniTags: {
    ':nth-child(1n) .tag': {
      padding: '2px 6px',
      fontSize: 10,
      lineHeight: '12px',
      borderRadius: 4,
      marginRight: 0,
    },
  },
});
